import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Hexagon, KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

// ---------------------------------------------------------------------------
// Force Password Change Page
//
// Mounted at `/force-password-change`. ProtectedRoute redirects any
// authenticated user with `must_change_password === true` here, no matter
// where they tried to navigate. The user cannot leave until they post a new
// password via POST /api/auth/change-password — which the backend uses to
// clear the flag.
// ---------------------------------------------------------------------------
export default function ForcePasswordChangePage() {
  const { user, refreshMe, logout } = useAuth();
  const navigate = useNavigate();

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  // If the user landed here but doesn't actually need to change their
  // password (e.g. typed the URL manually), bounce them to the dashboard.
  if (user && user.must_change_password === false) {
    return <Navigate to="/app/dashboard" replace />;
  }
  if (!user) return <Navigate to="/login" replace />;

  // Client-side strength check mirrors the backend policy.
  const issues = [];
  if (newPw.length < 8) issues.push("at least 8 characters");
  if (!/[A-Z]/.test(newPw)) issues.push("an uppercase letter");
  if (!/[a-z]/.test(newPw)) issues.push("a lowercase letter");
  if (!/[0-9]/.test(newPw)) issues.push("a number");
  if (!/[^A-Za-z0-9]/.test(newPw)) issues.push("a special character");
  const strong = issues.length === 0;
  const matches = newPw && newPw === confirmPw;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!currentPw || !newPw) return toast.error("Please fill both passwords");
    if (!matches) return toast.error("New passwords don't match");
    if (newPw === currentPw) return toast.error("New password must be different from the current one");
    if (newPw.length < 8) return toast.error("New password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      await refreshMe();
      toast.success("Password updated. Welcome to OmniDraftLab.");
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{
        background:
          "radial-gradient(ellipse 80% 60% at 20% 10%, rgba(34,211,238,0.08), transparent 60%), radial-gradient(ellipse 70% 50% at 85% 85%, rgba(251,113,133,0.06), transparent 60%), #04111A",
      }}
      data-testid="force-password-page"
    >
      <div className="w-full max-w-md">
        {/* Brand strip */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg grid place-items-center bg-gradient-to-br from-cyan-300 to-blue-500">
              <Hexagon className="h-4 w-4 text-[#04111A]" strokeWidth={2.4} />
            </div>
            <div>
              <div className="text-1 text-[15px] font-semibold tracking-tight leading-none">OmniDraftLab</div>
              <div className="text-3 text-[9px] uppercase tracking-[0.22em] mt-1">Secure Setup</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="force-password-signout"
            className="text-3 hover:text-1 text-xs uppercase tracking-[0.18em]"
          >
            Sign out
          </button>
        </div>

        {/* Card */}
        <div className="glass-strong rounded-2xl p-7 ring-1 ring-cyan-300/15">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-cyan-300/15 ring-1 ring-cyan-300/40 grid place-items-center">
              <KeyRound className="h-5 w-5 text-cyan-300" />
            </div>
            <div>
              <div className="text-3 text-[10px] uppercase tracking-[0.22em] font-medium">First login · password reset</div>
              <h1 className="text-1 text-xl font-semibold tracking-tight leading-tight">Choose your password</h1>
            </div>
          </div>

          <p className="text-2 text-sm mt-4 leading-relaxed">
            Welcome, <span className="text-1 font-medium">{user.full_name?.split(" ")[0] || user.email}</span>. Before you can use OmniDraftLab, please replace the temporary password your advisor sent you with one only you know.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-3.5">
            <div className="space-y-1">
              <Label className="text-2 text-xs">Current (temporary) password</Label>
              <Input
                data-testid="force-password-current"
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                autoComplete="current-password"
                className="bg-white/5 border-white/10 text-1"
                placeholder="The password you received in your invite"
                required
              />
            </div>

            <div className="space-y-1">
              <Label className="text-2 text-xs">New password</Label>
              <div className="relative">
                <Input
                  data-testid="force-password-new"
                  type={showNew ? "text" : "password"}
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  autoComplete="new-password"
                  className="bg-white/5 border-white/10 text-1 pr-10"
                  placeholder="At least 8 chars · upper · lower · number · symbol"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  data-testid="force-password-toggle"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-3 hover:text-1"
                  aria-label={showNew ? "Hide password" : "Show password"}
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {newPw && (
                <div className="text-[11px] mt-1 flex items-center gap-1.5" data-testid="force-password-strength">
                  {strong ? (
                    <span className="text-emerald-300 inline-flex items-center gap-1.5">
                      <ShieldCheck className="h-3 w-3" /> Strong password
                    </span>
                  ) : (
                    <span className="text-amber-300/90 italic">
                      Needs {issues.join(" · ")}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-2 text-xs">Confirm new password</Label>
              <Input
                data-testid="force-password-confirm"
                type={showNew ? "text" : "password"}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                autoComplete="new-password"
                className="bg-white/5 border-white/10 text-1"
                placeholder="Re-enter the new password"
                required
              />
              {confirmPw && !matches && (
                <div className="text-rose-300 text-[11px] italic mt-1">Passwords don't match yet.</div>
              )}
            </div>

            <Button
              type="submit"
              data-testid="force-password-submit"
              disabled={busy || !currentPw || !strong || !matches}
              className="cta-primary w-full mt-2"
            >
              {busy ? "Updating…" : "Update password & continue"}
            </Button>
          </form>

          <div className="mt-4 text-3 text-[11px] italic leading-relaxed">
            Your advisor cannot see your new password. If you forget it, ask them to reset it for you.
          </div>
        </div>
      </div>
    </div>
  );
}
